import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Icon,
  Box,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiCheckCircle, FiClipboard } from 'react-icons/fi';
import LogActivityModal from './LogActivityModal';
import { supabase, isSupabaseConfigured } from '../../utils/supabase';

/**
 * True when a completed task can be tied back to a contact (directly or via
 * its deal). Pages use this to decide whether to open the prompt at all.
 */
export const taskHasLoggableEntity = (task) => !!(
  task && (
    task.contactId ||
    task.dealId ||
    task.relatedTo?.type === 'contact' ||
    task.relatedTo?.type === 'deal'
  )
);

/**
 * Task Completion Activity Prompt
 *
 * Shown right after a task linked to a contact or deal is marked complete.
 * Asks whether to log the interaction as a CRM activity ("I made this call -
 * record it") and, on confirm, opens LogActivityModal with the task's contact
 * resolved, the notes seeded from the task title, and the linked deal
 * preselected when the task came from a deal.
 *
 * Contact resolution handles every task vintage:
 * - task.contact                            -> joined by crm_get_user_tasks (contact_id)
 * - task.contactId / relatedTo(contact).id  -> fetched from crm_contacts
 * - task.dealId / relatedTo(deal).id        -> the deal's contact via crm_deals
 * If nothing resolves (orphaned link), the prompt closes silently so task
 * completion behaves exactly as before.
 */
export default function TaskCompletionActivityPrompt({
  isOpen,
  onClose,
  task,
  onCreateTask,
}) {
  const [contact, setContact] = useState(null);
  const [deals, setDeals] = useState([]);
  const [defaultDealId, setDefaultDealId] = useState('');
  const [resolved, setResolved] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);

  const completedBg = useColorModeValue('green.50', 'green.900');
  const completedBorder = useColorModeValue('green.200', 'green.700');

  useEffect(() => {
    if (!isOpen) {
      setContact(null);
      setDeals([]);
      setDefaultDealId('');
      setResolved(false);
      setShowLogModal(false);
      return undefined;
    }
    if (!task || !isSupabaseConfigured()) {
      onClose();
      return undefined;
    }

    let cancelled = false;

    const resolveContact = async () => {
      try {
        const contactId =
          task.contactId || (task.relatedTo?.type === 'contact' ? task.relatedTo.id : null);
        const dealId =
          task.dealId || (task.relatedTo?.type === 'deal' ? task.relatedTo.id : null);

        let resolvedContact = null;
        let resolvedDeals = [];
        let preselectedDealId = '';

        if (contactId) {
          if (task.contact?.id === contactId) {
            resolvedContact = task.contact;
          } else {
            const { data } = await supabase
              .from('crm_contacts')
              .select('id, first_name, last_name, email')
              .eq('id', contactId)
              .maybeSingle();
            if (data) {
              resolvedContact = {
                id: data.id,
                firstName: data.first_name,
                lastName: data.last_name,
                email: data.email,
              };
            }
          }

          if (resolvedContact) {
            const { data: dealRows } = await supabase
              .from('crm_deals')
              .select('id, deal_name')
              .eq('contact_id', resolvedContact.id)
              .order('updated_at', { ascending: false });
            resolvedDeals = (dealRows || []).map((d) => ({ id: d.id, dealName: d.deal_name }));
          }
        } else if (dealId) {
          const { data: dealRow } = await supabase
            .from('crm_deals')
            .select('id, deal_name, crm_contacts!contact_id(id, first_name, last_name, email)')
            .eq('id', dealId)
            .maybeSingle();
          if (dealRow?.crm_contacts) {
            const c = dealRow.crm_contacts;
            resolvedContact = {
              id: c.id,
              firstName: c.first_name,
              lastName: c.last_name,
              email: c.email,
            };
            resolvedDeals = [{ id: dealRow.id, dealName: dealRow.deal_name }];
            preselectedDealId = dealRow.id;
          }
        }

        if (cancelled) return;

        if (!resolvedContact) {
          // Orphaned or missing link - completing the task stays a no-prompt action
          onClose();
          return;
        }

        setContact(resolvedContact);
        setDeals(resolvedDeals);
        setDefaultDealId(preselectedDealId);
        setResolved(true);
      } catch (error) {
        console.error('[TaskCompletionActivityPrompt] Could not resolve task contact:', error);
        if (!cancelled) onClose();
      }
    };

    resolveContact();
    return () => {
      cancelled = true;
    };
    // onClose intentionally omitted: parents pass inline closures
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, task?.id]);

  const contactName = contact
    ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim()
    : '';

  const handleConfirm = () => setShowLogModal(true);

  const handleLogModalClose = () => {
    setShowLogModal(false);
    onClose();
  };

  if (!task) {
    return null;
  }

  return (
    <>
      {/* Step 1: ask (only once the contact is known, so the name can be shown) */}
      <Modal isOpen={isOpen && resolved && !showLogModal} onClose={onClose} size="md" isCentered>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={2}>
              <Icon as={FiCheckCircle} color="green.500" />
              <Text>Task Completed!</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box
                p={4}
                bg={completedBg}
                borderRadius="md"
                borderWidth="1px"
                borderColor={completedBorder}
              >
                <Text fontSize="sm" fontWeight="semibold" mb={1}>
                  Completed Task
                </Text>
                <Text fontSize="sm" noOfLines={2}>
                  {task.title}
                </Text>
              </Box>
              <Text fontSize="sm">
                Would you like to log an activity for <strong>{contactName}</strong>? It will
                appear on their timeline and counts as an interaction.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Not now
            </Button>
            <Button colorScheme="teal" leftIcon={<FiClipboard />} onClick={handleConfirm}>
              Log Activity
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Step 2: the actual activity form */}
      <LogActivityModal
        isOpen={isOpen && showLogModal}
        onClose={handleLogModalClose}
        contact={contact}
        deals={deals}
        defaultDealId={defaultDealId}
        initialNotes={task.title ? `Completed task: "${task.title}"` : ''}
        source="task_completion"
        onCreateTask={onCreateTask}
      />
    </>
  );
}
